import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Tabs,
  Tab,
  Chip,
  Avatar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import CommunityPost from '../components/CommunityPost';

// Dummy data for caregivers and their posts
const dummyPosts = [
  {
    id: 1,
    author: {
      name: 'Sarah Johnson',
      avatar: 'https://i.pravatar.cc/150?img=1',
      role: 'Senior Caregiver',
    },
    content: `Just completed the Fall Prevention course and wanted to share some key insights! 
    The most valuable lesson I learned was about environmental modifications. I've already implemented 
    several changes in my patient's home, including better lighting and non-slip mats. Has anyone else 
    made similar changes and noticed improvements?`,
    timestamp: '2025-01-22T04:30:00.000Z',
    likes: 24,
    tags: ['Fall Prevention', 'Patient Safety', 'Best Practices'],
    comments: [
      {
        id: 101,
        author: {
          name: 'Michael Chen',
          avatar: 'https://i.pravatar.cc/150?img=2',
          role: 'Caregiver'
        },
        content: 'Great insights! I also found that regular balance exercises have made a huge difference for my elderly patients.',
        timestamp: '2025-01-22T05:00:00.000Z'
      }
    ]
  },
  {
    id: 2,
    author: {
      name: 'David Williams',
      avatar: 'https://i.pravatar.cc/150?img=3',
      role: 'Certified Caregiver',
    },
    content: `Question for experienced caregivers: How do you handle resistant patients who don't want 
    to use their walking aids? I'm working with a patient who keeps insisting they don't need their 
    walker, despite their history of falls. Any advice would be greatly appreciated!`,
    timestamp: '2025-01-22T03:15:00.000Z',
    likes: 15,
    tags: ['Patient Care', 'Challenges', 'Advice Needed'],
    comments: [
      {
        id: 201,
        author: {
          name: 'Emily Rodriguez',
          avatar: 'https://i.pravatar.cc/150?img=4',
          role: 'Senior Caregiver'
        },
        content: 'Try explaining the benefits in terms of maintaining their independence. I find this approach usually works better than focusing on the risks.',
        timestamp: '2025-01-22T03:45:00.000Z'
      }
    ]
  },
  {
    id: 3,
    author: {
      name: 'Lisa Thompson',
      avatar: 'https://i.pravatar.cc/150?img=5',
      role: 'Home Health Aide',
    },
    content: `Success story! After implementing the assessment techniques from Module 2, I identified 
    several fall risks for my patient that weren't previously documented. Working with their family, 
    we've made some changes to their daily routine and living space. It's been two months with no falls! 
    The comprehensive assessment really makes a difference.`,
    timestamp: '2025-01-22T02:00:00.000Z',
    likes: 32,
    tags: ['Success Story', 'Assessment', 'Patient Safety'],
    comments: [
      {
        id: 301,
        author: {
          name: 'James Wilson',
          avatar: 'https://i.pravatar.cc/150?img=6',
          role: 'Caregiver'
        },
        content: 'This is inspiring! Could you share more details about the specific changes you implemented?',
        timestamp: '2025-01-22T02:30:00.000Z'
      }
    ]
  },
  {
    id: 4,
    author: {
      name: 'Maria Garcia',
      avatar: 'https://i.pravatar.cc/150?img=7',
      role: 'Professional Caregiver',
    },
    content: `Quick tip: I created a checklist based on the course material for my morning safety rounds. 
    It helps ensure I don't miss anything important. I've attached it here - feel free to use and modify 
    it for your own needs! #ShareResources #CaregiverTips`,
    timestamp: '2025-01-21T23:45:00.000Z',
    likes: 45,
    tags: ['Resources', 'Tips', 'Organization'],
    images: ['https://example.com/checklist-image.jpg'],
    comments: [
      {
        id: 401,
        author: {
          name: 'Robert Kim',
          avatar: 'https://i.pravatar.cc/150?img=8',
          role: 'Caregiver'
        },
        content: 'This is exactly what I needed! Thank you for sharing!',
        timestamp: '2025-01-22T00:15:00.000Z'
      }
    ]
  }
];

const popularTags = [
  'Fall Prevention',
  'Patient Safety',
  'Best Practices',
  'Tips',
  'Success Story',
  'Questions',
  'Resources',
  'Discussion'
];

function Community() {
  const [posts, setPosts] = useState(dummyPosts);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const handleAddComment = (postId, newComment) => {
    setPosts(posts.map(post => 
      post.id === postId
        ? { ...post, comments: [...post.comments, newComment] }
        : post
    ));
  };

  const filterPosts = () => {
    if (!searchQuery) return posts;
    const query = searchQuery.toLowerCase();
    return posts.filter(post => 
      post.content.toLowerCase().includes(query) ||
      post.author.name.toLowerCase().includes(query) ||
      post.tags.some(tag => tag.toLowerCase().includes(query))
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
          Caregiver Community
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Connect, share experiences, and learn from fellow caregivers
        </Typography>
      </Box>

      {/* Search and Filter */}
      <Box sx={{ mb: 4 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search discussions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              New Post
            </Button>
          </Box>

          {/* Popular Tags */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Popular Tags:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {popularTags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  onClick={() => setSearchQuery(tag)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Content Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
            }
          }}
        >
          <Tab label="All Posts" />
          <Tab label="Questions" />
          <Tab label="Success Stories" />
          <Tab label="Resources" />
        </Tabs>
      </Box>

      {/* Posts List */}
      <Box>
        {filterPosts().map(post => (
          <CommunityPost
            key={post.id}
            post={post}
            onAddComment={handleAddComment}
          />
        ))}
      </Box>
    </Container>
  );
}

export default Community;
